import { useId, useState, type CSSProperties, type InputHTMLAttributes } from "react";
import { colors, fontWeight } from "../design-system";
import { AppText } from "./AppText";

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "style"> & {
  /**
   * Message d'erreur À CÔTÉ du champ fautif : bordure sos600 + texte dessous.
   * La validation près du champ (règle Base) remplace le message global posé
   * à l'autre bout de l'écran.
   */
  error?: string;
  /**
   * Libellé visible. Nouveauté web : sans `label` ni `aria-label`, un champ est
   * muet pour un lecteur d'écran — le `placeholder` NE FAIT PAS office de
   * libellé (il disparaît à la saisie). Le natif s'en tirait avec le contexte
   * visuel ; ici c'est un défaut d'accessibilité.
   */
  label?: string;
  style?: CSSProperties;
};

/**
 * Champ de saisie du design system — 3 états visuels :
 * repos (bordure gray-200) → focus (bordure navy, on sait OÙ on tape) →
 * erreur (bordure sos600 + message). L'erreur PRIME sur le focus : corriger un
 * champ fautif ne doit pas faire disparaître l'alerte pendant la frappe.
 *
 * Le lien programmatique entre le champ, son libellé et son message d'erreur
 * (`aria-describedby`) est ce qui fait annoncer l'erreur au moment où
 * l'utilisateur atteint le champ, et pas seulement à qui la voit.
 */
export function Input({ error, label, onFocus, onBlur, id, style, ...props }: InputProps) {
  const [focused, setFocused] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  const borderColor = error ? colors.sos600 : focused ? colors.navy800 : colors.gray200;

  return (
    <div>
      {label && (
        <AppText as="label" variant="label" htmlFor={inputId} className="mb-1.5 block">
          {label}
        </AppText>
      )}

      <div
        style={{
          minHeight: 56,
          borderRadius: 12, // radius.input
          backgroundColor: colors.gray50,
          border: `1px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          // La bordure change d'état : sans transition, le passage au focus
          // « claque ». 120ms = token `motion.duration.fast`.
          transition: "border-color 120ms",
        }}
      >
        <input
          {...props}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
          style={{
            flex: 1,
            width: "100%",
            paddingLeft: 16,
            paddingRight: 16,
            fontSize: 15,
            color: colors.black,
            fontWeight: fontWeight.semiBold,
            background: "transparent",
            border: "none",
            // La bordure de focus est portée par le cadre parent : l'anneau de
            // focus par défaut du navigateur ferait doublon à l'intérieur.
            outline: "none",
            ...style,
          }}
        />
      </div>

      {error ? (
        <AppText
          id={errorId}
          // `role="alert"` fait annoncer l'erreur dès son apparition, sans
          // attendre que l'utilisateur revienne sur le champ.
          role="alert"
          variant="tiny"
          color={colors.sos600}
          className="mt-1 ml-1 block"
        >
          {error}
        </AppText>
      ) : null}
    </div>
  );
}
